import React, { useEffect } from 'react';
import getSubproductTotalExpense from 'services/products/calculations/getSubproductTotalExpense';
import { getSellingPriceQuantityFactor } from 'services/products/calculations/getSellingPriceForMargin';
import numericStringToFloat from 'services/numbers/numericStringToFloat';
import formatGroupedNumber from 'services/numbers/formatGroupedNumber';
import { Col, Row } from 'reactstrap';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

import apiProducts from 'api/products';
import CollapseIcon from 'components/layout/icons/CollapseIcon';
import getShownDifference from 'services/numbers/getShownDifference';
import getSellingPricePerUnit from 'services/products/calculations/getSellingPricePerUnit';
import getContributionMarginRatio from 'services/products/calculations/getContributionMarginRatio';
import getExpensePerUnit from 'services/products/calculations/getExpensePerUnit';
import getExpenseTotal from 'services/products/calculations/getExpenseTotal';
import getFormStateHandler from 'services/forms/getFormStateHandler';
import getSellingPriceTotal from 'services/products/calculations/getSellingPriceTotal';
import IProduct from 'api/interfaces/products/IProductGet';
import IValueWithLabel from 'interfaces/forms/IValueWithLabel';
import NumberWithDropdownInputField from 'components/layout/forms/NumberWithDropdownInputField';
import NumberWithUnitInputField from 'components/layout/forms/NumberWithUnitInputField';
import SearchDropdown from 'components/layout/forms/SearchDropdown';
import SelectInputFieldControlled from 'components/layout/forms/SelectInputFieldControlled';
import TextInputFieldNoLabel from 'components/layout/forms/TextInputFieldNoLabel';
import translations from 'services/translations/translations';
import TrashIcon from 'components/layout/icons/TrashIcon';
import { fetchCategories, fetchUnits } from 'store/products/actions';

import {
  MAX_QUANTITY_DIGITS,
  MAX_WASTE_PERCENTAGE_DIGITS,
} from 'services/offers/inputLimits';
import showInCents from 'services/numbers/showInCents';
import formatFloatForDisplay from 'services/numbers/formatFloatForDisplay';
import { withTargetMargin } from '../targetMargin';
import priceMaterialFromDetails, {
  getDetailTotalsFactor,
  getSellingPriceFromDetails,
  isPricedPerUnitFromDetails,
  isSellingPriceTyped,
  withSellingPriceFromDetails,
} from './priceMaterialFromDetails';
import getMarginRatioClassName from '../getMarginRatioClassName';
import PositionUnitPrice from '../PositionUnitPrice';
import { PackageStepper } from '../PackageSizes';
import useMarginInputs from '../useMarginInputs';
import getProductCategoryOptions from '../../../../services/getProductCategoryOptions';
import getSellingPriceUnitOptions from '../../../../services/getSellingPriceUnitOptions';
import getUnitOptions from '../../../../services/getUnitOptions';
import IDetail from '../../IDetail';
import ISubproduct from '../../ISubproduct';
import SubproductDetails from './SubproductDetails';

const Subproduct = ({
  locked,
  onRemoveDetail,
  opened,
  quantity,
  reordering,
  removeSubproduct,
  targetMarginPercent,
  setSubproduct,
  subproduct,
  toggleOpened,
}: {
  locked: boolean;
  // Several rows can have their details open at the same time
  onRemoveDetail: (
    subproduct: ISubproduct,
    detail: IDetail,
    index: number
  ) => void;
  opened: boolean;
  quantity: number;
  // The details can be reordered as well while the row order is being changed
  reordering: boolean;
  removeSubproduct: (subproduct: ISubproduct) => void;
  // The target margin % of the window for the rows following it
  targetMarginPercent: string;
  setSubproduct: (subproduct: ISubproduct) => void;
  subproduct: ISubproduct;
  toggleOpened: () => void;
}): JSX.Element => {
  // A new row prices itself for its target margin until its price is set. A material
  // priced per unit from its details gets its selling price again when its waste changes;
  // otherwise the price stays as it is (also a price typed by the user, and the price of
  // a material priced the earlier way)
  const setPricedSubproduct = (updated: ISubproduct) => {
    const priced = withTargetMargin(
      subproduct,
      updated,
      getSubproductTotalExpense,
      targetMarginPercent
    );

    setSubproduct(
      priced.wastePercentage !== subproduct.wastePercentage &&
        isPricedPerUnitFromDetails(priced)
        ? priceMaterialFromDetails(priced)
        : priced
    );
  };

  const { t } = useTranslation([translations.common, translations.products]);

  const dispatch = useDispatch();
  const categories = useSelector((state: any) => state.products.categories);
  const units = useSelector((state: any) => state.products.units);

  const collapsed = opened;

  useEffect(() => {
    dispatch(fetchCategories());
    dispatch(fetchUnits());
  }, [dispatch]);

  useEffect(() => {
    const expenseUnit =
      subproduct.expenseUnit === '' ||
      subproduct.expenseUnit === subproduct.quantityUnit
        ? subproduct.expenseUnit
        : subproduct.quantityUnit;

    const sellingPriceUnit =
      subproduct.sellingPriceUnit === '' ||
      subproduct.sellingPriceUnit === subproduct.quantityUnit
        ? subproduct.sellingPriceUnit
        : subproduct.quantityUnit;

    const changed =
      subproduct.expenseUnit !== expenseUnit ||
      subproduct.sellingPriceUnit !== sellingPriceUnit;

    if (!changed) {
      return;
    }

    setPricedSubproduct({
      ...subproduct,
      expenseUnit,
      sellingPriceUnit,
    });
  }, [setSubproduct, subproduct]);

  const handleOtherFields = getFormStateHandler(
    subproduct,
    setPricedSubproduct
  );

  // A selling price the user gives is the price of one unit of the position (e.g. €/m²)
  // and is not multiplied by the quantity of the row: a material priced per unit from its
  // details (e.g. €/jm) becomes a sum when its price is typed
  const handleFormState = (event: any) => {
    if (
      event.target.name === 'sellingPrice' &&
      subproduct.calculatePriceFromDetails &&
      isPricedPerUnitFromDetails(subproduct)
    ) {
      setPricedSubproduct({
        ...subproduct,
        sellingPrice: event.target.value,
        sellingPriceUnit: '',
      });
      return;
    }

    handleOtherFields(event);
  };
  const handleProductChange = (product: IValueWithLabel) => {
    setPricedSubproduct({ ...subproduct, product });
  };

  const contributionMarginRatio = getContributionMarginRatio([], [subproduct]);
  const expenseForUnit = getExpensePerUnit([], [subproduct]);
  const expenseTotal = getExpenseTotal([], [subproduct], quantity);
  const sellingPriceTotal = getSellingPriceTotal([], [subproduct], quantity);

  // The margins shown: the shown selling price minus the shown expense, so they add up
  const contributionMarginForUnit = getShownDifference(
    getSellingPricePerUnit([], [subproduct]),
    expenseForUnit
  );
  const contributionMarginTotal = getShownDifference(
    sellingPriceTotal,
    expenseTotal
  );

  const marginRatioClassName = getMarginRatioClassName(
    contributionMarginRatio,
    subproduct.sellingPrice
  );

  const marginInputs = useMarginInputs({
    contributionMargin: contributionMarginForUnit,
    contributionMarginRatio,
    expense: expenseForUnit,
    quantityFactor: getSellingPriceQuantityFactor(
      subproduct.sellingPriceUnit,
      numericStringToFloat(subproduct.quantity)
    ),
    setSellingPrice: (sellingPrice: string) =>
      setPricedSubproduct({ ...subproduct, sellingPrice }),
  });

  const toggleCollapse = () => toggleOpened();

  const loadProducts = async (inputValue: string) => {
    const list = (await apiProducts.get(
      100,
      0,
      inputValue,
      undefined,
      false
    )) as IProduct[];
    return list.map((product: IProduct) => ({
      label: [null, ''].includes(product.displayName)
        ? product.name
        : product.displayName,
      value: product.id,
    }));
  };

  const offerIsLocked = () => locked;

  // The waste of the row on the amount of material needed
  const rowWasteFactor =
    1 + numericStringToFloat(subproduct.wastePercentage) / 100;

  const sellingPriceUnitOptions = getSellingPriceUnitOptions(
    subproduct.quantityUnit,
    subproduct.key.toString(),
    t
  );

  return (
    <>
      <Row className="subproduct-row">
        <Col>
          <SelectInputFieldControlled
            disabled={offerIsLocked()}
            id={`product-subproduct-${subproduct.key}-category`}
            name="category"
            onChange={handleFormState}
            options={getProductCategoryOptions(categories, t)}
            required
            value={subproduct.category}
          />
        </Col>
        <Col>
          {subproduct.isCustomSubproduct ? (
            <TextInputFieldNoLabel
              disabled={offerIsLocked()}
              id={`product-subproduct-${subproduct.key}-product`}
              name="customSubproduct"
              onChange={handleFormState}
              value={
                subproduct.customSubproduct === null
                  ? ''
                  : subproduct.customSubproduct
              }
            />
          ) : (
            <SearchDropdown
              className="text-field search-field"
              disabled={offerIsLocked()}
              id={`product-subproduct-${subproduct.key}-product`}
              loadOptions={loadProducts}
              onChange={handleProductChange}
              name="product"
              placeholder={t(`${translations.common}:Search`)}
              required
              value={subproduct.product}
            />
          )}
        </Col>
        <Col className="icon-column">
          <CollapseIcon
            collapsed={collapsed}
            onClick={() => toggleCollapse()}
            size="sm"
          />
        </Col>
        <Col>
          <NumberWithDropdownInputField
            disabled={offerIsLocked()}
            dropdownName="quantityUnit"
            dropdownOnChange={handleFormState}
            dropdownOptions={getUnitOptions(units, t)}
            dropdownValue={subproduct.quantityUnit}
            id={`product-subproduct-${subproduct.key}-quantity`}
            maxIntegerDigits={MAX_QUANTITY_DIGITS}
            numberName="quantity"
            numberOnChange={handleFormState}
            numberValue={subproduct.quantity}
            required
          />
          {/* Packages for the whole position; a package more or less changes the
              quantity of the row as if typed */}
          <PackageStepper
            disabled={offerIsLocked() || quantity <= 0}
            productId={
              subproduct.isCustomSubproduct
                ? null
                : Number(subproduct.product?.value) || null
            }
            setTotal={(total) =>
              handleFormState({
                target: {
                  name: 'quantity',
                  value: formatFloatForDisplay(
                    total / (quantity * rowWasteFactor),
                    6
                  ),
                },
              })
            }
            total={
              numericStringToFloat(subproduct.quantity) *
              quantity *
              rowWasteFactor
            }
            unit={subproduct.quantityUnit}
          />
        </Col>
        <Col>
          <NumberWithDropdownInputField
            disabled={offerIsLocked()}
            dropdownDisabled
            dropdownName="expenseUnit"
            dropdownOnChange={handleFormState}
            dropdownOptions={sellingPriceUnitOptions}
            dropdownValue={subproduct.expenseUnit}
            id={`product-subproduct-${subproduct.key}-expense`}
            numberDisabled={subproduct.calculatePriceFromDetails}
            numberName="expense"
            numberOnChange={handleFormState}
            // Calculated from the details with hidden decimals, shown in cents
            numberValue={
              subproduct.calculatePriceFromDetails
                ? showInCents(subproduct.expense)
                : subproduct.expense
            }
            required
          />
        </Col>
        <Col className="small-column">
          <NumberWithUnitInputField
            disabled={offerIsLocked()}
            id={`product-subproduct-${subproduct.key}-waste-percentage`}
            maxIntegerDigits={MAX_WASTE_PERCENTAGE_DIGITS}
            name="wastePercentage"
            onChange={handleFormState}
            required
            unit="%"
            value={subproduct.wastePercentage}
          />
        </Col>
        <Col className="small-column">
          <NumberWithUnitInputField
            disabled
            unit="€"
            value={formatGroupedNumber(expenseForUnit)}
          />
        </Col>
        <Col>
          <NumberWithDropdownInputField
            disabled={offerIsLocked()}
            dropdownDisabled
            dropdownName="sellingPriceUnit"
            dropdownOnChange={handleFormState}
            dropdownOptions={sellingPriceUnitOptions}
            dropdownValue={subproduct.sellingPriceUnit}
            id={`product-subproduct-${subproduct.key}-selling-price`}
            numberName="sellingPrice"
            numberOnChange={handleFormState}
            numberValue={
              subproduct.calculatePriceFromDetails
                ? showInCents(subproduct.sellingPrice)
                : subproduct.sellingPrice
            }
            required
          />
          {/* The source of the price: typed instead of the price of the details */}
          {isSellingPriceTyped(subproduct) ? (
            <button
              className="typed-price"
              disabled={offerIsLocked()}
              onClick={() =>
                setPricedSubproduct(withSellingPriceFromDetails(subproduct))
              }
              title={t(`${translations.calculationWindow}:Typed price info`, {
                price: formatGroupedNumber(
                  getSellingPriceFromDetails(subproduct)
                ),
              })}
              type="button"
            >
              {t(`${translations.calculationWindow}:Typed price`, {
                price: formatGroupedNumber(
                  getSellingPriceFromDetails(subproduct)
                ),
              })}
            </button>
          ) : null}
        </Col>
        {/* The selling price of the row for one unit of the position */}
        <Col className="small-column">
          <PositionUnitPrice price={getSellingPricePerUnit([], [subproduct])} />
        </Col>
        <Col className="small-column">
          <NumberWithUnitInputField
            disabled={offerIsLocked()}
            onBlur={marginInputs.eur.onBlur}
            onChange={marginInputs.eur.onChange}
            unit="€"
            value={marginInputs.eur.value}
          />
        </Col>
        <Col className="small-column">
          <NumberWithUnitInputField
            disabled
            unit="€"
            value={formatGroupedNumber(expenseTotal)}
          />
        </Col>
        <Col className="small-column">
          <NumberWithUnitInputField
            disabled
            unit="€"
            value={formatGroupedNumber(sellingPriceTotal)}
          />
        </Col>
        <Col className="small-column">
          <NumberWithUnitInputField
            disabled
            unit="€"
            value={formatGroupedNumber(contributionMarginTotal)}
          />
        </Col>
        <Col className={`small-column ${marginRatioClassName}`}>
          <NumberWithUnitInputField
            disabled={offerIsLocked()}
            onBlur={marginInputs.percent.onBlur}
            onChange={marginInputs.percent.onChange}
            unit="%"
            value={marginInputs.percent.value}
          />
        </Col>
        <Col className="icon-column">
          {offerIsLocked() ? null : (
            <TrashIcon onClick={() => removeSubproduct(subproduct)} />
          )}
        </Col>
      </Row>
      <SubproductDetails
        collapsed={collapsed}
        locked={locked}
        onRemoveDetail={(detail: IDetail, index: number) =>
          onRemoveDetail(subproduct, detail, index)
        }
        reordering={reordering}
        subproduct={subproduct}
        targetMarginPercent={targetMarginPercent}
        setSubproduct={setSubproduct}
        // The detail totals add up to the totals of the material
        subproductQuantity={getDetailTotalsFactor(subproduct, quantity)}
      />
    </>
  );
};

export default Subproduct;
